import React from "react";
import styled from "styled-components";
import media from "responsive";
import MenuItem from "./MenuItem";
import MenuItemNoPrice from "./MenuItemNoPrice";

/**
 * Generic category renderer that handles all three groupBy strategies:
 * - "none": Simple item list with optional banner
 * - "price": Dynamic price grouping with banner template
 * - "subcategory": Groups by subcategoryId with subcategory banners
 */
export default function CategoryRenderer({ category, items }) {
  const { groupBy, displayPrice, piecesPerOrder, subcategories } = category;

  // Helper to parse price string to number for sorting
  const parsePrice = (priceStr) => {
    if (!priceStr) return 0;
    return parseFloat(priceStr.replace("$", ""));
  };

  // Render items based on displayPrice setting
  const renderItems = (itemsToRender, showPrice = displayPrice) => {
    return itemsToRender.map((item) =>
      showPrice ? (
        <MenuItem key={item.id} item={item} />
      ) : (
        <MenuItemNoPrice key={item.id} item={item} />
      )
    );
  };

  // Strategy: none - Simple item list
  // Return Fragment so items flow directly into parent's flex container
  if (groupBy === "none") {
    return <>{renderItems(items)}</>;
  }

  // Strategy: price - Dynamic price grouping
  if (groupBy === "price") {
    // Get unique prices and sort numerically
    const uniquePrices = [...new Set(items.map((item) => item.price))]
      .filter(Boolean)
      .sort((a, b) => parsePrice(a) - parsePrice(b));

    return (
      <Container>
        {uniquePrices.map((price) => {
          const itemsAtPrice = items.filter((item) => item.price === price);
          const bannerText = piecesPerOrder
            ? `1 ORDER ${piecesPerOrder} PCS ${price}`
            : price;

          return (
            <React.Fragment key={price}>
              <ItemGroup>{bannerText}</ItemGroup>
              <Menus>{renderItems(itemsAtPrice, false)}</Menus>
            </React.Fragment>
          );
        })}
      </Container>
    );
  }

  // Strategy: subcategory - Groups by subcategoryId
  if (groupBy === "subcategory" && subcategories) {
    // Sort subcategories by displayOrder if available
    const sortedSubcategories = [...subcategories].sort(
      (a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)
    );

    return (
      <Container>
        {sortedSubcategories.map((subcategory) => {
          const subcategoryItems = items.filter(
            (item) => item.subcategoryId === subcategory.id
          );

          // Skip if no items in this subcategory
          if (subcategoryItems.length === 0) return null;

          // Use subcategory's displayPrice if defined, otherwise fall back to category's
          const showPrice =
            subcategory.displayPrice !== undefined
              ? subcategory.displayPrice
              : displayPrice;

          return (
            <React.Fragment key={subcategory.id}>
              <ItemGroup>{subcategory.banner}</ItemGroup>
              <Menus>{renderItems(subcategoryItems, showPrice)}</Menus>
            </React.Fragment>
          );
        })}
      </Container>
    );
  }

  // Fallback: render items without grouping
  return <>{renderItems(items)}</>;
}

const Container = styled.div`
  width: 100%;
`;

const ItemGroup = styled.div`
  border: 1px solid #ddd;
  background-color: #99c0a3;
  font-size: 1.6em;
  color: #333;
  text-align: left;
  width: auto;
  display: inline-block;
  font-weight: 600;
  border-radius: 10px;
  padding: 10px 15px;

  ${media.mobileL`
    font-size: 1em;
    padding: 8px 12px;
    margin-top: 20px;
  `}

  ${media.galaxyFold`
    font-size: 0.9em;
    padding: 5px 10px;
  `}
`;

const Menus = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: auto;
  padding: 45px 0;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;

  ${media.mobileL`
    padding-top: 25px;
    padding-bottom: 0px;
  `}

  ${media.galaxyFold`
    padding-top: 10px;
    padding-bottom: 0px;
  `}
`;
